type MemberSprite = {
  duration: number;
  image: HTMLImageElement;
};

const memberSpriteData: Array<[string, number]> = [
  ["melo-pixel-sprite-v1.png", 1.72],
  ["peac-pixel-sprite-v1.png", 2.1],
  ["kiw-pixel-sprite-v2.png", 1.35],
  ["banan-pixel-sprite-v2.png", 2.2],
  ["guav-pixel-sprite-v2.png", 2.5],
  ["papay-pixel-sprite-v2.png", 2],
  ["duria-pixel-sprite-v2.png", 2.5],
  ["mang-pixel-sprite-v2.png", 2.3],
  ["coconu-pixel-sprite-v2.png", 2.6],
  ["pomel-pixel-sprite-v2.png", 2.4],
  ["citro-pixel-sprite-v2.png", 2.7],
  ["lemo-pixel-sprite-v2.png", 3],
];

export function initializeMemberKeyVisual(container: HTMLElement, options: { wallpaper?: boolean; paused?: boolean } = {}): () => void {
  const canvas = container.querySelector("canvas");
  if (!(canvas instanceof HTMLCanvasElement)) return () => undefined;

  const context = canvas.getContext("2d");
  if (!context) return () => undefined;
  const targetCanvas: HTMLCanvasElement = canvas;
  const drawingContext: CanvasRenderingContext2D = context;

  const members: MemberSprite[] = memberSpriteData.map(([file, duration]) => {
    const image = new Image();
    image.decoding = "async";
    image.src = `/assets/member-directory/${file}`;
    return { duration, image };
  });
  const settings = options.wallpaper
    ? { memberScale: 1.05, density: 0.8, motionSpeed: 0.3, iconSpeed: 0.65 }
    : { memberScale: 1.55, density: 1.75, motionSpeed: 1.25, iconSpeed: 0.75 };
  const baseSpacing = 112;
  const phiSquared = (3 + Math.sqrt(5)) / 2;
  const morphSeconds = 30;
  const frameCount = 9;
  const reducedMotion = options.paused ?? matchMedia("(prefers-reduced-motion: reduce)").matches;
  let inView = true;
  let width = 1;
  let height = 1;
  let animationFrame = 0;
  let lastPaintAt = 0;
  let lastClockAt = performance.now();
  let motionTime = 0;
  let iconTime = 0;
  let disposed = false;

  const modulo = (number: number, divisor: number) => ((number % divisor) + divisor) % divisor;
  const randomBetween = (minimum: number, maximum: number) => minimum + Math.random() * (maximum - minimum);

  function resize() {
    const rect = container.getBoundingClientRect();
    const ratioLimit = settings.density >= 1.8 ? 1.35 : 1.75;
    const ratio = Math.min(devicePixelRatio || 1, ratioLimit);
    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);
    targetCanvas.width = Math.round(width * ratio);
    targetCanvas.height = Math.round(height * ratio);
    drawingContext.setTransform(ratio, 0, 0, ratio, 0, 0);
    drawingContext.imageSmoothingEnabled = false;
  }

  function advanceClocks(now: number) {
    if (!reducedMotion) {
      const delta = Math.max(0, now - lastClockAt) / 1000;
      motionTime += delta * settings.motionSpeed;
      iconTime += delta * settings.iconSpeed;
    }
    lastClockAt = now;
  }

  function paint(now: number) {
    if (disposed) return;
    advanceClocks(now);
    const responsiveScale = Math.max(0.48, Math.min(width / 980, height / 650));
    const spacing = baseSpacing / settings.density * responsiveScale;
    const iconSize = 78 * settings.memberScale * responsiveScale;
    const flowCells = Math.max(1, Math.round(settings.density * 3));
    const phase = modulo(motionTime, morphSeconds) / morphSeconds;
    const e = phiSquared ** phase;
    const k = (e - 1 / e) / Math.sqrt(5);
    const a = (e + 1 / e) / 2 - k / 2;
    const m00 = k + a;
    const m01 = k;
    const m10 = k;
    const m11 = a;
    const centerX = width / 2 + spacing * flowCells * m01;
    const centerY = height / 2 + spacing * flowCells * (m11 - 1);
    const corners: Array<[number, number]> = [
      [-iconSize - centerX, -iconSize - centerY],
      [width + iconSize - centerX, -iconSize - centerY],
      [-iconSize - centerX, height + iconSize - centerY],
      [width + iconSize - centerX, height + iconSize - centerY],
    ];
    const gridCorners: Array<[number, number]> = corners.map(([x, y]) => [
      (m11 * x - m01 * y) / spacing,
      (-m10 * x + m00 * y) / spacing,
    ]);
    const minI = Math.floor(Math.min(...gridCorners.map(([i]) => i))) - 2;
    const maxI = Math.ceil(Math.max(...gridCorners.map(([i]) => i))) + 2;
    const minJ = Math.floor(Math.min(...gridCorners.map(([, j]) => j))) - 2;
    const maxJ = Math.ceil(Math.max(...gridCorners.map(([, j]) => j))) + 2;
    const visibleMembers: Array<{ x: number; y: number; member: MemberSprite; frame: number }> = [];

    drawingContext.clearRect(0, 0, width, height);
    for (let i = minI; i <= maxI; i += 1) {
      for (let j = minJ; j <= maxJ; j += 1) {
        const x = centerX + spacing * (m00 * i + m01 * j);
        const y = centerY + spacing * (m10 * i + m11 * j);
        if (x < -iconSize || x > width + iconSize || y < -iconSize || y > height + iconSize) continue;

        const shiftedJ = j + flowCells;
        const invariant = i * i - shiftedJ * shiftedJ - i * shiftedJ;
        const memberIndex = modulo(invariant, 13) * 7 % members.length;
        const member = members[memberIndex];
        if (!member || !member.image.complete || member.image.naturalWidth === 0) continue;
        const frame = Math.floor(modulo(iconTime, member.duration) / member.duration * frameCount);
        visibleMembers.push({ x, y, member, frame });
      }
    }

    visibleMembers.sort((left, right) => left.y - right.y || left.x - right.x);
    for (const { x, y, member, frame } of visibleMembers) {
      const frameWidth = member.image.naturalWidth / frameCount;
      drawingContext.drawImage(member.image, frame * frameWidth, 0, frameWidth, member.image.naturalHeight, x - iconSize / 2, y - iconSize / 2, iconSize, iconSize);
    }
  }

  function tick(now: number) {
    if (disposed) return;
    const targetFps = options.wallpaper ? 24 : settings.density >= 1.8 ? 45 : 60;
    if (now - lastPaintAt >= 1000 / targetFps) {
      lastPaintAt = now;
      paint(now);
    }
    animationFrame = requestAnimationFrame(tick);
  }

  function shuffle() {
    const now = performance.now();
    advanceClocks(now);
    settings.memberScale = randomBetween(1.05, 1.7);
    settings.density = randomBetween(1.15, 2.2);
    settings.motionSpeed = randomBetween(0.65, 3.2);
    settings.iconSpeed = randomBetween(0.4, 2);
    container.classList.toggle("is-light");
    resize();
    paint(now);
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    shuffle();
  }

  function handleVisibility() {
    cancelAnimationFrame(animationFrame);
    lastClockAt = performance.now();
    if (!document.hidden && !reducedMotion && inView) {
      lastPaintAt = 0;
      animationFrame = requestAnimationFrame(tick);
    }
  }

  for (const { image } of members) {
    const ready = () => paint(performance.now());
    if (image.complete && image.naturalWidth > 0) ready();
    else image.addEventListener("load", ready, { once: true });
  }
  const observer = new ResizeObserver(() => {
    resize();
    paint(performance.now());
  });
  observer.observe(container);
  const visibilityObserver = new IntersectionObserver(([entry]) => {
    inView = Boolean(entry?.isIntersecting);
    handleVisibility();
  });
  visibilityObserver.observe(container);
  if (!options.wallpaper) {
    container.addEventListener("click", shuffle);
    container.addEventListener("keydown", handleKeydown);
  }
  document.addEventListener("visibilitychange", handleVisibility);
  resize();
  paint(performance.now());
  if (!reducedMotion) animationFrame = requestAnimationFrame(tick);

  return () => {
    disposed = true;
    cancelAnimationFrame(animationFrame);
    observer.disconnect();
    visibilityObserver.disconnect();
    container.removeEventListener("click", shuffle);
    container.removeEventListener("keydown", handleKeydown);
    document.removeEventListener("visibilitychange", handleVisibility);
  };
}
